import { useId, type ReactNode } from "react";
import { WarningOctagonIcon } from "@phosphor-icons/react";

export interface FieldControlProps {
  id: string;
  "aria-describedby"?: string;
  "aria-invalid"?: true;
}

/** Visible label, optional help text, and an inline error wired with aria-describedby. */
export function Field({
  label,
  optional,
  help,
  error,
  children,
}: {
  label: string;
  optional?: boolean;
  help?: ReactNode;
  error?: string | null;
  children: (props: FieldControlProps) => ReactNode;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  const describedBy = [help ? helpId : null, error ? errId : null].filter(Boolean).join(" ") || undefined;
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
        {optional ? <span className="opt"> (optional)</span> : null}
      </label>
      {children({ id, "aria-describedby": describedBy, "aria-invalid": error ? true : undefined })}
      {help ? (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      ) : null}
      {error ? (
        <p className="field__error" id={errId}>
          <WarningOctagonIcon size={14} aria-hidden="true" style={{ marginTop: 3, flex: "none" }} />
          {error}
        </p>
      ) : null}
    </div>
  );
}
